import React from 'react';
import { Link } from 'react-router-dom';
import ParrotBrandMark from '../components/ParrotBrandMark';

const APP_STORE_URL =
  'https://apps.apple.com/us/app/parrot-speech-improvement/id1457391626';
const CONTACT_EMAIL = 'hello@speakparrot.com';

const Home = () => (
  <div className="min-h-screen bg-parrot-yellow flex flex-col">
    {/* Top nav */}
    <header className="flex items-center justify-between px-6 sm:px-16 pt-8">
      <div className="flex items-center gap-3">
        <ParrotBrandMark className="h-12 w-12" />
        <span className="text-parrot-black font-bold tracking-widest text-sm">
          PARROT
        </span>
      </div>
      <nav className="flex items-center gap-5">
        <Link
          to="/help"
          className="text-parrot-black font-semibold text-sm hover:text-parrot-blue transition-colors"
        >
          Help
        </Link>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="text-parrot-black font-semibold text-sm hover:text-parrot-blue transition-colors"
        >
          Contact Us
        </a>
      </nav>
    </header>

    {/* Hero */}
    <main className="flex-1 flex items-center px-6 sm:px-16 py-12">
      <div className="w-full max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        {/* Left column */}
        <div className="order-2 md:order-1">
          <h1 className="text-[88px] sm:text-[120px] leading-none font-black text-parrot-blue tracking-tight">
            PARROT
          </h1>

          <div className="mt-8 text-xl sm:text-2xl font-bold text-parrot-black tracking-wide leading-relaxed">
            <div>
              FIND <Highlight>YOUR VOICE</Highlight>.
            </div>
            <div className="mt-1">
              BE <Highlight>CONFIDENT</Highlight>.
            </div>
          </div>

          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-10 inline-flex items-center gap-3 bg-parrot-black text-white px-6 py-3 rounded-full hover:scale-[1.02] transition-transform shadow-lg"
          >
            <AppleLogo className="w-7 h-7" />
            <div className="text-left leading-tight">
              <div className="text-[11px] opacity-80">Download on the</div>
              <div className="text-xl font-semibold">App Store</div>
            </div>
          </a>

          <p className="mt-10 text-parrot-black/80 text-xs sm:text-sm tracking-widest uppercase max-w-md leading-relaxed">
            Great speakers do not give speeches, they give conversations.
            Having compelling conversations takes self awareness, passion,
            and practice.
          </p>
        </div>

        {/* Right column — phone mockup */}
        <div className="order-1 md:order-2 flex justify-center">
          <PhoneMockup />
        </div>
      </div>
    </main>

    {/* Footer */}
    <footer className="border-t border-parrot-black/10 py-6 px-6 sm:px-16">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-parrot-black/70 text-sm">
          © {new Date().getFullYear()} Parrot. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <Link
            to="/help"
            className="text-parrot-black/80 text-sm hover:text-parrot-blue transition-colors"
          >
            Help
          </Link>
          <Link
            to="/legal"
            className="text-parrot-black/80 text-sm hover:text-parrot-blue transition-colors"
          >
            Legal
          </Link>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-parrot-black/80 text-sm hover:text-parrot-blue transition-colors"
          >
            Contact
          </a>
        </div>
      </div>
    </footer>
  </div>
);

const Highlight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="bg-parrot-blue text-parrot-yellow px-2 py-0.5 inline-block">
    {children}
  </span>
);

const AppleLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 384 512"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M318.7 268.7c-.2-37 16.6-65 50.5-85.6-19-27.2-47.5-42.2-85.2-45.1-35.7-2.8-74.7 20.8-89 20.8-15.1 0-49.6-19.8-77.4-19.8C75.6 140.8 8 184.6 8 273.8 8 313 14.5 353.4 27.6 395.6 45 451.7 100.2 504 132.9 502.9c20-1 34.2-13.7 60.4-13.7 25.4 0 38.5 13.7 60.8 13.7 33 .5 83.1-46.8 100.6-102.8-93.7-44.3-86-127.7-66.7-156.5zm-65.4-205.1C291.6 21.3 286.6 0 281.4 0c-5.8 0-22.9 11.4-37.4 25.3-15.1 14.5-26.6 36-21.4 56.7 16.6 1.2 35.4-9.5 50.7-23z" />
  </svg>
);

const PhoneMockup = () => (
  <div className="relative w-[260px] sm:w-[300px]">
    <div className="bg-parrot-black rounded-[44px] p-2 shadow-2xl">
      <div
        className="bg-parrot-yellow rounded-[36px] flex flex-col px-5 py-8 relative overflow-hidden"
        style={{ aspectRatio: '9 / 19.5' }}
      >
        {/* Dynamic island */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-7 bg-parrot-black rounded-full" />

        {/* Timer */}
        <div className="mt-12 text-center">
          <p className="text-parrot-blue text-sm font-bold">9s left</p>
        </div>

        {/* Topic */}
        <div className="mt-6 text-center px-2">
          <p className="text-parrot-blue text-lg font-bold leading-tight">
            When was the last time you tried something new?
          </p>
        </div>

        {/* Waveform */}
        <div className="flex-1 flex items-center justify-center">
          <svg
            viewBox="0 0 240 120"
            className="w-full"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 60 Q 30 0 60 60 T 120 60 T 180 60 T 240 60"
              stroke="#4A90E2"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <path
              d="M0 60 Q 40 25 80 60 T 160 60 T 240 60"
              stroke="#4A90E2"
              strokeOpacity="0.45"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M0 60 Q 50 90 100 60 T 200 60"
              stroke="#4A90E2"
              strokeOpacity="0.3"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* Caption */}
        <div className="text-center pb-2">
          <p className="text-parrot-blue text-sm font-semibold">constantly</p>
        </div>
      </div>
    </div>
  </div>
);

export default Home;
